'use client'

import type { Rewards } from '@morpho-org/dapp-core'

import { safeParseNumber } from '@morpho-org/blue-sdk-viem'
import { Skeleton } from '@repo/ui'
import { useMemo } from 'react'

import type { MerklTarget } from '@/helpers/reward/fetchMerklOpportunityUrl'

import { getParsedValue } from '@/helpers/formatters'
import { normalizeVaultApyRewards } from '@/helpers/rewards'
import { isVaultV2RateCapped, toVaultV2ApyGross } from '@/helpers/vaultV2RateCap'
import { useMerklOpportunityUrl } from '@/hooks/reward/useMerklOpportunityUrl'
import { useVaultV2ApyQuery, type VaultV2ApyQueryInput } from '@/hooks/useVaultV2ApyQuery'

import RewardsHoverCard, { type RewardsHoverCardProps } from './RewardsHoverCard'

export type { MerklTarget }

interface MerklRewardsHoverCardProps extends RewardsHoverCardProps {
  merklContext?: MerklTarget | null
  vaultV2ApyInput?: VaultV2ApyQueryInput
}

const MerklRewardsHoverCardBody = ({
  merklContext,
  rewards,
  vaultV2ApyInput: _vaultV2ApyInput,
  ...props
}: MerklRewardsHoverCardProps) => {
  // Merkl only campaigns the borrow side of a market, so a lend-rate row must not link to a
  // campaign that pays it nothing; the rewards fall back to their explorer links. Vault
  // campaigns are supply-side, so `kind: 'vault'` still resolves.
  const isBorrowOnlyTarget = merklContext?.kind === 'market' && props.variant === 'supply'
  const merklOpportunityUrl = useMerklOpportunityUrl(isBorrowOnlyTarget ? null : merklContext)

  const enrichedRewards = useMemo((): Rewards[] | null | undefined => {
    if (!merklOpportunityUrl || !rewards) return rewards

    return rewards.map(reward => ({
      ...reward,
      merklOpportunityUrl
    }))
  }, [rewards, merklOpportunityUrl])

  return <RewardsHoverCard rewards={enrichedRewards} {...props} />
}

const QueriedVaultV2ApyHoverCard = ({
  vaultV2ApyInput,
  ...props
}: MerklRewardsHoverCardProps & { vaultV2ApyInput: VaultV2ApyQueryInput }) => {
  const { data, isPending, isError } = useVaultV2ApyQuery(vaultV2ApyInput)
  if (isPending) {
    return (
      <div
        aria-label="Loading APY breakdown"
        className="px-sm py-sm min-h-[220px] w-[275px] space-y-3"
        data-testid="vault-v2-apy-loading"
      >
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-sm w-full" />
        <Skeleton className="h-sm w-5/6" />
        <Skeleton className="h-sm w-full" />
        <Skeleton className="h-sm w-2/3" />
      </div>
    )
  }
  if (isError || !data) {
    return (
      <div className="px-sm py-sm w-[275px] text-xs" data-testid="vault-v2-apy-error">
        APY breakdown unavailable.
      </div>
    )
  }

  const maxRate = getParsedValue(data.maxRate, safeParseNumber)
  const grossApy = toVaultV2ApyGross(data)

  return (
    <MerklRewardsHoverCardBody
      {...props}
      rewards={normalizeVaultApyRewards(data.rewards)}
      apy={getParsedValue(data.netApyExcludingRewards, safeParseNumber)}
      netApy={getParsedValue(data.netApy, safeParseNumber)}
      performanceFee={getParsedValue(data.fees.performanceFee, safeParseNumber)}
      managementFee={getParsedValue(data.fees.managementFee, safeParseNumber)}
      maxRate={maxRate}
      maxApy={data.maxRate}
      isRateCapped={isVaultV2RateCapped(grossApy, data.maxRate)}
      apyBreakdown={data.apyBreakdown}
    />
  )
}

const MerklRewardsHoverCard = (props: MerklRewardsHoverCardProps) => {
  if (props.vaultV2ApyInput) {
    return <QueriedVaultV2ApyHoverCard {...props} vaultV2ApyInput={props.vaultV2ApyInput} />
  }
  return <MerklRewardsHoverCardBody {...props} />
}

export default MerklRewardsHoverCard
